export default {
  title: 'Payments',
  description: 'View and manage all payment records',

  labels: {
    date: 'Date',
    invoice: 'Invoice',
    customer: 'Customer',
    unit: 'Unit',
    amount: 'Amount',
    method: 'Method',
    reference: 'Note / Reference',
    verification_status: 'Verification',
    status: 'Status',
  },

  filters: {
    all_methods: 'All Methods',
    all_periods: 'All Periods',
    custom_range: 'Custom Range',
    billing_period: 'Billing Period',
    date_from: 'From',
    date_to: 'To',
    search_placeholder: 'Search reference...',
    all_verification_statuses: 'All Statuses',
    verification_status: 'Verification Status',
    fraud_suspect: 'Fraud Suspect',
    unidentified: 'Unidentified payer',
    // EP-161 §2.4: kiosk parking payments only (channel=parking_kiosk)
    channel: {
      label: 'Channel',
      all: 'All',
      parking_kiosk: 'Parking',
    },
  },

  methods: {
    cash: 'Cash',
    transfer: 'Bank Transfer',
    promptpay: 'PromptPay',
    credit_card: 'Credit Card',
    qr_image: 'QR Payment (Parking)',
  },

  actions: {
    print_selected: 'Print Receipt ({{count}})',
    print_all: 'Print All',
    print_all_description: 'Generate receipts for all {{count}} filtered payments and open as PDF in a new tab.',
    export: 'Export',
    download_receipt: 'Receipt',
    verify: 'Verify',
    reject: 'Reject',
    retry_verification: 'Retry Verification',
    new_payment: 'New Payment',
    edit_payment: 'Edit Payment',
    delete_payment: 'Delete Payment',
    flag_fraud: 'Flag as Fraud',
  },

  fraud: {
    confirm_title: 'Flag as Fraud',
    confirm_body: 'This will record the sender details and slip fingerprint in the fraud registry. Future slips from the same sender will be blocked automatically. Continue?',
    flagged_success: 'Payment flagged as fraud and added to the fraud registry.',
  },

  form: {
    invoice: 'Invoice',
    select_invoice: 'Invoice',
    invoice_search_placeholder: 'Search an unpaid invoice…',
    no_invoice_option: 'No invoice',
  },

  confirm: {
    delete: 'Delete this payment? This will update the invoice balance.',
  },

  errors: {
    admin_only: 'Only an admin or owner can create or edit payments.',
    verified_amount_locked: 'Amount and payment method cannot be changed on a verified payment.',
    property_required: 'Please choose a property for this payment.',
    property_not_found: 'That property was not found in this account.',
    unidentified_payment_only: 'This payment already has a customer or an invoice.',
    customer_not_found: 'Please select a customer.',
    invoice_customer_mismatch: 'That invoice belongs to a different customer.',
  },

  identify_payer: {
    action: 'Identify payer',
    title: 'Identify payer',
    hint: 'Attach a customer, and optionally the invoice it paid, to this payment.',
    customer_label: 'Customer',
    invoice_label: 'Invoice (optional)',
    submit: 'Assign payer',
    success: 'Payer identified.',
  },

  quick_pay: {
    title: 'Record Payment',
    // F2 (T149-088 QA fix): the merged form also pays an existing invoice now,
    // so this can no longer say "without an invoice".
    description: 'Record a payment, with or without an invoice.',
    property_label: 'Property',
    property_placeholder: 'Select a property…',
    property_required: 'Please choose a property.',
    no_property_title: 'No property yet',
    no_property_hint: 'A payment must be recorded against a property. Create your first property to get started.',
    no_property_cta: 'Create a property',
    customer_label: 'Customer',
    customer_placeholder: 'Search customer by name or phone…',
    customer_required: 'Please select a customer.',
    unknown_payer_label: 'Unknown payer',
    amount_label: 'Amount ({{currency}})',
    reference_placeholder: 'Cheque number, transfer ref…',
    submit: 'Record Payment',
    success: 'Payment recorded successfully.',
    not_available_condo: 'Standalone payment recording is not available for condominium accounts.',
    date_future_warning: 'This date is in the future',
  },

  not_found: 'Payment not found.',

  empty: {
    title: 'No payments found',
    description: 'Payment records will appear here once payments are recorded against invoices.',
    no_results: 'No payments match your search or filters.',
    clear_filters: 'Clear filters',
  },

  setup_guide: {
    title: 'Welcome to Payments',
    description: 'Create an invoice to record a payment against',
    step_1_title: 'Set up a property and customer',
    step_1_description: 'Create at least one property with a unit and add your tenant as a customer.',
    step_2_title: 'Create a lease',
    step_2_description: 'Link the customer to a unit through a lease to activate billing.',
    step_3_title: 'Generate an invoice, then record payment',
    step_3_description: 'Generate an invoice from the Invoices page, then use Record Payment on that invoice.',
    go_to_invoices: 'Go to Invoices',
    import: 'Import',
    download_sample: 'Download sample',
    seed_sample: 'Seed sample data',
    seed_sample_confirm: 'This will create: 1 property + 1 customer + 1 lease + 1 invoice. You can then record a payment. Continue?',
    seed_sample_success: 'Sample data created. You can now record a payment against the sample invoice.',
    seed_sample_noop: 'You already have invoices. Sample data was not added.',
  },

  verification: {
    title: 'Slip Verification',
    status: 'Status',
    provider: 'Provider',
    trans_ref: 'Transaction Reference',
    amount: 'Verified Amount',
    sender: 'Sender',
    sender_account: 'Sender Account',
    receiver: 'Receiver',
    receiver_account: 'Receiver Account',
    transfer_date: 'Transfer Date',
    rejection_reasons: 'Rejection Reasons',
    verify_confirm: 'Confirm verification of this payment slip?',
    verify_reason_label: 'Reason (optional)',
    reject_reason: 'Reason for rejection',
    reject_reason_placeholder: 'Describe why this slip is being rejected...',
    retry_confirm: 'Re-submit this payment slip for automatic verification?',
    slip_images: 'Slip Images',
    slip_image_alt: 'Slip image {{count}}',
    slip_image_lightbox_alt: 'Slip',
    // EP-161: the ML reading beside a parking slip, for reference only.
    reference_heading: 'Automatic slip reading (for reference only)',
    no_reading: 'No slip reading yet',
    reject_reason_visible_to_visitor: 'This reason is shown to the driver on the payment page.',
    override_section: 'Admin Override',
    no_verification_data: 'No verification data available',
    gateway_confirmed: 'Paid & validated via {{provider}}',
    stripe_refund_note: 'FlowRent does not refund through Stripe. To refund, transfer the money back to the tenant yourself and record it as an expense.',
    manual_recorded: 'Recorded manually. No slip verification needed.',
    provider_label_ml: 'ML reading',
    provider_label_rdcw: 'Verified by RDCW',
    provider_label_other: 'Verified by {{provider}}',
    ml_match_matched: 'ML: match',
    ml_match_unmatched: 'ML: no match',
    // EP-145 A5: one label per EP-143 ml_match outcome. The two above stay for
    // rows written under the EP-101 vocabulary, which is still in production JSON.
    ml_match_agree: 'ML: agrees',
    ml_match_disagree: 'ML: disagrees',
    ml_match_solo_verify: 'ML: verified alone',
    ml_match_rdcw_fail: 'ML: passed, bank rejected',
    ml_reading_detail: 'ML Reading Detail',
    reading_confidence: 'Reading Confidence',
    banks: {
      scb: 'SCB',
      kbank: 'KBank',
      ktb: 'KTB',
      bbl: 'BBL',
      bay: 'Krungsri (BAY)',
      ttb: 'TTB',
      gsb: 'GSB',
      kkp: 'KKP',
      promptpay_generic: 'PromptPay (generic)',
      unknown: 'Unknown',
    },
    field_amount: 'Amount',
    field_datetime: 'Datetime',
    field_bank: 'Bank',
    field_sender: 'Sender',
    field_receiver: 'Receiver',
    field_trans_ref: 'Transaction Ref',
    field_ref1: 'Ref1',
    manual_check_hint: 'Compare each value the ML read against the slip image, then tick the fields that match. Ticked fields prefill the verify reason.',
    ocr_value: 'ML read',
    expected_value: 'Expected',
    check_field: 'Check',
    checked_field: 'Checked',
    fields_checked_reason: 'Manually checked against slip: {{fields}}',
    corrections_hint: 'Fix any field the ML misread. Enter the value exactly as printed on the slip. Saved corrections become training labels.',
    correction_placeholder: 'Correct value…',
    save_corrections: 'Save corrections',
    corrections_saved: 'Corrections saved',
    model_versions: 'Model Versions',
    model_bank_classifier: 'Bank Classifier',
    model_field_ocr: 'Field OCR',
    gate_evidence_section: 'Gate Evidence',
    gate_chip_pass: 'Pass',
    gate_chip_fail: 'Fail',
    gate_chip_unavailable: 'N/A',
    gate_names: {
      g0: 'G0 Readable',
      g1: 'G1 QR Decode',
      g2: 'G2 OCR Core',
      g3: 'G3 QR–OCR Match',
      g4: 'G4 Recipient',
      g5: 'G5 Amount',
      g6: 'G6 Freshness',
      g7: 'G7 Dedup',
      g8: 'G8 Fraud Registry',
      p9: 'P9 Amount Cap',
    },
    fraud_suspect_banner: 'Fraud suspect: this slip triggered a fraud signal. Review carefully before verifying.',
    statuses: {
      pending: 'Pending',
      verifying: 'Verifying',
      verified: 'Verified',
      rejected: 'Rejected',
      failed: 'Failed',
      missing_slip: 'Missing Slip',
      manual: 'Manual',
      gateway: 'Stripe',
    },
    reasons: {
      slip_invalid: 'Slip image is not a valid bank transfer slip',
      duplicate_trans_ref: 'Duplicate transaction reference. This slip was already used',
      amount_mismatch: 'Amount on slip does not match payment amount',
      ref1_mismatch: 'Payment reference (Ref1) on slip does not match this payment',
      image_retrieval_failed: 'Slip image is corrupted or could not be retrieved',
      // EP-145 A1: gate-outcome reason terms emitted by SlipGateLadder.
      // One key per outcome the ladder can report, so a reason never renders
      // as a raw term. Keep in sync with SlipGateLadder::REASON_TERMS.
      unknown_bank: 'Bank could not be identified from the slip image',
      low_confidence: 'Slip reading confidence is below the required level',
      no_qr_trans_ref: 'No QR transaction reference could be read from the slip',
      amount_missing_or_low_confidence: 'Amount could not be read from the slip clearly enough',
      datetime_missing_or_low_confidence: 'Transfer date and time could not be read from the slip clearly enough',
      qr_ocr_mismatch: 'The QR code and the printed text on the slip do not agree',
      qr_ocr_review: 'The QR code and the printed text on the slip differ slightly',
      qr_ocr_match_unavailable: 'The QR and text cross-check could not be run on this slip',
      g3_not_viable_for_bank: 'The QR and text cross-check is not yet reliable for this bank, so it was skipped',
      trans_ref_low_confidence: 'The transaction reference on the slip could not be read confidently enough to cross-check the QR',
      no_recipient_registry: 'No receiving bank accounts are registered, so the recipient could not be checked',
      no_receiver_account_in_reading: 'Recipient account number could not be read from the slip',
      recipient_not_registered: 'Slip was paid to an account that is not in your registered receiving accounts',
      amount_parsed_missing: 'Amount could not be read from the slip',
      no_anchor_for_rental_static_transfer: 'This transfer has no payment QR to check the timing against',
      slip_datetime_missing: 'Slip does not show a transfer date and time',
      slip_datetime_unparseable: 'Transfer date and time on the slip could not be understood',
      slip_predates_intent: 'Slip is dated before this payment was created',
      slip_expired: 'Slip is dated outside the allowed window for this payment',
      no_trans_ref: 'Slip has no transaction reference to check for duplicates',
      account_id_unresolvable: 'Payment could not be linked to an account, so duplicate checking was skipped',
      fraud_registry_hit: 'Slip matches an entry in the fraud registry',
      amount_cap: 'Amount is above the automatic verification limit',
    },
  },

  // EP-161 §2.4: the plate and zone of a parking payment
  columns: {
    plate: 'Plate',
  },

  detail: {
    parking_block: {
      title: 'Parking',
      plate: 'Plate',
      zone: 'Zone',
      open_sessions: 'View on Parking today',
    },
  },
}
